import { useContext } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  alpha,
  Stack,
  lighten,
  Divider,
  styled,
  useTheme,
  IconButton,
  Tooltip
} from '@mui/material';
import MenuTwoToneIcon from '@mui/icons-material/MenuTwoTone';
import { SidebarContext } from 'src/contexts/SidebarContext';

import HeaderButtons from './Buttons';
import HeaderUserbox from './Userbox';
import HeaderMenu from './Menu';
import Logo from 'src/components/LogoSign';

// Category color mappings (copied from SidebarMenu)
const iconColors = {
  overview: '#b0b4ba',
  dashboards: '#1975ff',
  management: '#9c27b0',
  accounts: '#43a047',
  components: '#0288d1',
  extra: '#ffa000',
  trip: '#FF5722' // deep orange
};

// Helper: get category by route (copied from SidebarMenu)
function getCategoryByRoute(route) {
  if (route.startsWith('/home')) return 'overview';
  if (route.startsWith('/overview')) return 'overview';
  if (route.startsWith('/cv')) return 'overview';
  if (route.startsWith('/dashboards')) return 'dashboards';
  if (route.startsWith('/management/profile')) return 'accounts';
  if (route.startsWith('/management')) return 'management';
  if (route.startsWith('/accounts')) return 'accounts';
  if (route.startsWith('/components')) return 'components';
  if (route.startsWith('/trip')) return 'trip';
  return 'extra';
}

const HeaderWrapper = styled(Box)(
  ({ theme }) => `
    height: ${theme.header.height};
    color: ${theme.header.textColor};
    z-index: 9; /* Above the sidebar */
    background-color: ${theme.header.background};
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    border-bottom: 1px solid ${theme.palette.divider};
    box-shadow: 0 1px 4px 0 var(--category-header-shadow, #b0b4ba1A);
  `
);

function Header() {
  const { sidebarToggle, toggleSidebar } = useContext(SidebarContext);
  const theme = useTheme();
  const location = useLocation();
  const category = getCategoryByRoute(location.pathname);
  const dividerColor = theme.palette.divider;

  return (
    <HeaderWrapper
      display="flex"
      alignItems="center"
      sx={{
        '--category-header-color': dividerColor,
        '--category-header-shadow': `${dividerColor}1A`
      }}
    >
      {/* Left side - Persistent hamburger + logo */}
      <Box display="flex" alignItems="center" sx={{ pl: 1.5, pr: 2 }}>
        <Tooltip arrow title={sidebarToggle ? 'Show menu' : 'Hide menu'}>
          <IconButton
            color="primary"
            onClick={toggleSidebar}
            size="small"
            sx={{
              width: 44,
              height: 44,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.1)
              }
            }}
            aria-label="toggle sidebar"
          >
            <MenuTwoToneIcon />
          </IconButton>
        </Tooltip>
        <Box sx={{ ml: 0.5 }}>
          <Logo />
        </Box>
      </Box>

      {/* Center desktop menu (hidden on mobile) */}
      <Stack
        direction="row"
        divider={<Divider orientation="vertical" flexItem sx={{ borderColor: dividerColor, borderWidth: 2 }} />}
        alignItems="center"
        spacing={2}
        sx={{ flex: 1, minWidth: 0, display: { xs: 'none', md: 'flex' } }}
      >
        <HeaderMenu />
      </Stack>

      {/* Right side buttons */}
      <Box
        display="flex"
        alignItems="center"
        sx={{
          pr: 1.5,
          ml: 'auto', // push to far right when desktop menu hidden
          color:
            theme.palette.mode === 'dark'
              ? '#fff'
              : sidebarToggle
              ? '#fff'
              : theme.palette.text.primary,
          gap: 0.5
        }}
      >
        {/* Condensed overflow (contains search, notifications, theme) */}
        <HeaderButtons />
        {/* User avatar always at far right */}
        <HeaderUserbox
          userboxColor={
            theme.palette.mode === 'dark' ? '#fff' : sidebarToggle ? '#fff' : undefined
          }
        />
      </Box>
    </HeaderWrapper>
  );
}

export default Header;
